import {
  TicketComment,
  TicketDetail,
  TicketDocument,
  TicketLog,
  TicketMovementHistory,
  TicketTask,
  TicketTimelineEntry,
} from '../models/ticket.model';
import { DETAIL_OPERATION, isOpeningOperation, isWebDetailOperation } from '@app/shared/utils';

/**
 * Aparência única de movimentação. `movement_history` (alteração feita na web) e
 * `ticket_details.operation` (movimentação do HelpDesk) são tabelas diferentes por razão
 * histórica, mas para quem lê a timeline são a mesma coisa — logo, mesmo ícone, cor e rótulo.
 */
const MOVEMENT_ICON = 'pi pi-pencil';
const MOVEMENT_COLOR = '#0ea5e9';
const MOVEMENT_TITLE = 'Movimentação';

/** Nome do autor de um ticket_details — o campo `user` pode vir string ou objeto. */
export function detailUserName(detail: TicketComment): string {
  if (detail.user) {
    if (typeof detail.user === 'string') {
      return detail.user;
    }
    if (typeof detail.user === 'object' && 'name' in detail.user) {
      return (detail.user as { name: string }).name;
    }
  }
  return 'Usuário';
}

/**
 * True quando a linha carrega texto escrito por uma pessoa — o card de comentário.
 *
 * `movement` (espelho de alteração de campo) e `notation` (início/fim de apontamento) são registros
 * do sistema: mesmo trazendo `observation`, o texto ali é do próprio registro, não um comentário.
 * Todo o resto — inclusive movimentação do Delphi que veio com uma nota junto — tem a `observation`
 * como algo que alguém digitou.
 */
export function isCommentDetail(detail: TicketComment): boolean {
  const operation = detail.operation?.trim();
  if (operation === DETAIL_OPERATION.movement || operation === DETAIL_OPERATION.notation) {
    return false;
  }
  return !!detail.observation?.trim();
}

/** True quando a linha carrega uma movimentação registrada pelo sistema — o card de movimentação. */
export function isMovementDetail(detail: TicketComment): boolean {
  const operation = detail.operation?.trim();
  return !!operation && operation !== DETAIL_OPERATION.comment;
}

/**
 * Movimentação registrada em `ticket_details` (transferência, troca de situação, apontamento).
 *
 * Os dois campos são mutuamente exclusivos aqui, e nunca concatenados: quando `operation` é um
 * código de máquina do TicketCenter ele não é texto para exibir, e o que vale é a `observation`;
 * nas movimentações do Delphi a frase está em `operation`, e uma `observation` que venha junto é
 * nota de quem atendeu — essa vira card de comentário próprio, ver `isCommentDetail`.
 */
export function movementToEntry(detail: TicketComment): TicketTimelineEntry {
  const operation = detail.operation?.trim();
  const description = isWebDetailOperation(operation)
    ? (detail.observation?.trim() ?? '')
    : (operation ?? '');

  return {
    type: 'history',
    date: detail.created_at,
    icon: MOVEMENT_ICON,
    color: MOVEMENT_COLOR,
    title: MOVEMENT_TITLE,
    description,
    user: detailUserName(detail),
    data: detail,
  };
}

/** Alteração de campo feita na web (movement_history) — mesmo card das movimentações. */
export function movementHistoryToEntry(mh: TicketMovementHistory): TicketTimelineEntry {
  return {
    type: 'history',
    date: mh.created_at,
    icon: MOVEMENT_ICON,
    color: MOVEMENT_COLOR,
    title: MOVEMENT_TITLE,
    description: mh.description ?? '',
    user: mh.user_name ?? '',
  };
}

export function commentToEntry(detail: TicketComment): TicketTimelineEntry {
  const userName = detailUserName(detail);

  // Extrair URLs de imagens do comentário
  const imageUrls: string[] = [];
  if (detail.observation) {
    const matches = detail.observation.match(/https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg)/gi);
    if (matches) {
      imageUrls.push(...matches);
    }
  }

  const entry: TicketTimelineEntry = {
    type: 'comment',
    date: detail.created_at,
    icon: 'pi pi-comment',
    color: '#3b82f6',
    title: 'Comentário',
    description: detail.observation ?? '',
    user: userName,
    data: detail,
    isPublic: !!detail.pub,
  };

  // Adicionar URLs de imagens extraídas ao data
  if (imageUrls.length > 0) {
    (entry.data as any).imageUrls = imageUrls;
  }

  return entry;
}

export function taskToEntry(task: TicketTask): TicketTimelineEntry {
  return {
    type: 'task',
    date: task.created_at,
    icon: 'pi pi-check-square',
    color: '#8b5cf6',
    title: task.finished ? 'Tarefa concluída' : 'Tarefa criada',
    description: task.description,
    user: task.user?.name ?? '',
    data: task,
  };
}

export function documentToEntry(doc: TicketDocument): TicketTimelineEntry {
  let userName = '';

  if (doc.user) {
    userName = typeof doc.user === 'string' ? doc.user : (doc.user as { name: string }).name;
  } else if (!doc.user_id) {
    userName = 'Usuário não identificado';
  }

  return {
    type: 'attachment',
    date: doc.created_at ?? '',
    icon: 'pi pi-paperclip',
    color: '#f59e0b',
    title: 'Anexo',
    description: doc.observation?.trim() || undefined,
    user: userName,
    data: doc,
  };
}

export function documentationToEntry(doc: any): TicketTimelineEntry {
  return {
    type: 'documentation',
    date: doc.created_at ?? new Date().toISOString(),
    icon: 'pi pi-file',
    color: '#06b6d4',
    title: 'Documentação',
    description: doc.title || 'Sem título',
    user: doc.created_by_user?.name ?? '',
    data: doc,
  };
}

export function logToEntry(log: TicketLog): TicketTimelineEntry {
  const isOpen = !!log.start_date && !log.end_date;
  return {
    type: 'log',
    date: log.start_date ?? log.created_at,
    icon: isOpen ? 'pi pi-spin pi-clock' : 'pi pi-clock',
    color: isOpen ? '#f97316' : '#64748b',
    title: isOpen ? 'Apontamento em andamento' : 'Apontamento',
    description: log.description ?? '',
    user: log.user?.name ?? '',
    data: log,
    startDate: log.start_date,
    endDate: log.end_date,
    isOpen,
  };
}

/**
 * Monta a timeline completa do chamado, em ordem decrescente de data.
 *
 * Compartilhado pelo modal de detalhe e pela página em aba: as duas telas mostram exatamente a
 * mesma timeline, e manter duas cópias já tinha feito as regras divergirem.
 */
/**
 * Traz do payload recém-buscado TODAS as fontes que buildTicketTimeline() lê, preservando o resto
 * do chamado que já está em memória.
 *
 * Existe aqui, e não nos componentes, porque a lista de fontes tem que ficar ao lado de quem as
 * consome: os merges viviam duplicados no modal e na página, enumerando campo por campo, e os dois
 * esqueceram `movement_history` e `documentations`. O dado chegava da API e era descartado no
 * merge — alteração de tarefa nunca aparecia na timeline, porque `movement_history` é a única fonte
 * dela (o espelho `ticket_details.movement` é removido do payload pelo backend).
 *
 * Ao acrescentar uma fonte nova em buildTicketTimeline, acrescente aqui também.
 */
export function mergeTimelineSources(current: TicketDetail, fresh: TicketDetail): TicketDetail {
  return {
    ...current,
    details: fresh.details ?? current.details,
    tasks: fresh.tasks ?? current.tasks,
    documents: fresh.documents ?? current.documents,
    documentations: fresh.documentations ?? current.documentations,
    ticket_logs: fresh.ticket_logs ?? current.ticket_logs,
    movement_history: fresh.movement_history ?? current.movement_history,
  };
}

export function buildTicketTimeline(ticket: TicketDetail): TicketTimelineEntry[] {
  const entries: TicketTimelineEntry[] = [];
  // Hora real da abertura, quando o HelpDesk registrou a linha correspondente — ver o item
  // 'opened' no fim da função.
  let openedAt: string | null = null;

  // Comentários (web e HelpDesk) e movimentações — tudo vem de ticket_details.
  //
  // As duas classificações são independentes, e não um if/else: uma linha do HelpDesk pode trazer
  // ao mesmo tempo a frase da movimentação em `operation` e a nota do atendente em `observation`.
  // Nesse caso ela rende dois cards, um em cada balde, cada um mostrando só o campo que lhe cabe —
  // é o que separa "o que a pessoa escreveu" de "o que o sistema registrou" na hora de filtrar.
  if (ticket.details) {
    for (const detail of ticket.details) {
      const operation = detail.operation?.trim();

      // Espelho da alteração de campo: já aparece na timeline via movement_history, e o backend
      // normalmente já filtra (scopeWithoutMovementMirror). Aqui é defesa em profundidade.
      if (operation === DETAIL_OPERATION.movement) {
        continue;
      }

      // A abertura já tem o item dedicado 'opened' no fim desta função — não repetir aqui,
      // só aproveitar a hora dela.
      if (isOpeningOperation(operation)) {
        openedAt = openedAt ?? detail.created_at;
        continue;
      }

      if (isCommentDetail(detail)) {
        entries.push(commentToEntry(detail));
      }

      if (isMovementDetail(detail)) {
        entries.push(movementToEntry(detail));
      }
    }
  }

  for (const task of ticket.tasks ?? []) {
    entries.push(taskToEntry(task));
  }

  for (const doc of ticket.documents ?? []) {
    entries.push(documentToEntry(doc));
  }

  for (const doc of ticket.documentations ?? []) {
    entries.push(documentationToEntry(doc));
  }

  for (const log of ticket.ticket_logs ?? []) {
    entries.push(logToEntry(log));
  }

  for (const mh of ticket.movement_history ?? []) {
    entries.push(movementHistoryToEntry(mh));
  }

  entries.push({
    type: 'opened',
    // Vale o mais antigo entre o `created_at` do chamado e a linha "Abertura da Chamada":
    // a abertura não pode ser posterior a nenhum registro do chamado. Em chamado vindo do
    // HelpDesk o `created_at` é o instante em que o sync inseriu a linha (~1,5 min depois
    // da abertura), então quem vale é a linha; em chamado criado pela web, o inverso.
    date:
      openedAt && new Date(openedAt).getTime() < new Date(ticket.created_at).getTime()
        ? openedAt
        : ticket.created_at,
    icon: 'pi pi-plus-circle',
    color: '#10b981',
    title: 'Chamado aberto',
    description: ticket.summary ?? '',
    user: ticket.user_opened?.name ?? '',
  });

  entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return entries;
}
